/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {FilterDrawerCollapseAllIcon} from '../../icons/current/FilterDrawerCollapseAllIcon';
import {FilterDrawerExpandAllIcon} from '../../icons/current/FilterDrawerExpandAllIcon';
import {MaterialExitCloseIcon} from '../../icons/current/MaterialExitCloseIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {IconButton} from '../buttons/IconButton';

interface FilterDrawerHeaderProps {
  allExpanded: boolean;
  classes: Record<string, string>;
  onClose: () => void;
  onToggleAll: () => void;
  title: string;
}

/**
 * Displays the title, expand/collapse all action, and close control.
 *
 * ```typescript
 * import FilterDrawerHeader from '@ion/dls/src/components/drawer/FilterDrawerHeader';
 *
 * ...
 *
 * <FilterDrawerHeader allExpanded={allExpanded}
 *   classes={classes}
 *   onClose={closeDrawer}
 *   onToggleAll={toggleAll}
 *   title='Tracking Filter' />
 * ```
 */
export const FilterDrawerHeader = memo(({
  allExpanded,
  classes,
  onClose,
  onToggleAll,
  title
}: FilterDrawerHeaderProps) => (
  <div className={classes.header}>
    <div className={classes.titleGroup}>
      <span className={classes.title}>{title}</span>
    </div>
    <div className={classes.headerActions}>
      <button type='button'
        className={classes.expandAllButton}
        onClick={onToggleAll}
        aria-label={allExpanded
          ? translate('filters:drawer.collapseAll')
          : translate('filters:drawer.expandAll')}>
        <Render>
          <Render.If condition={allExpanded}>
            <FilterDrawerCollapseAllIcon color={dlsColor('grey1')} />
          </Render.If>
          <Render.Else>
            <FilterDrawerExpandAllIcon color={dlsColor('grey1')} />
          </Render.Else>
        </Render>
        <span className={classes.expandAllLabel}>
          <Render>
            <Render.If condition={allExpanded}>
              {translate('filters:drawer.collapseAll')}
            </Render.If>
            <Render.Else>
              {translate('filters:drawer.expandAll')}
            </Render.Else>
          </Render>
        </span>
      </button>
      <IconButton icon={<MaterialExitCloseIcon color={dlsColor('grey1')} size={IconSize.small} />}
        className={classes.closeButton}
        aria-label={translate('common:close')}
        disableHover
        onClick={onClose} />
    </div>
  </div>
));

export default FilterDrawerHeader;
